import { useState } from "react";
import { CalendarIcon } from "lucide-react";
import { es } from "date-fns/locale";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  enmascararFecha,
  fechaDesdeIso,
  formatearFecha,
  isoDesdeFecha,
  isoDesdeTextoChileno,
} from "@/lib/fechas";
import { cn } from "@/lib/utils";

/**
 * Campo de fecha del DS: SE TIPEA, y el calendario es el atajo.
 *
 * Reemplaza a `<input type="date">`, que NO se puede localizar: el formato que
 * dibuja —`mm/dd/yyyy`— lo decide el idioma del navegador, no la página. Ni el
 * CSS ni el `lang` del documento lo arreglan en Firefox ni en Safari, que leen
 * la región del sistema operativo. Un usuario chileno con el navegador en
 * inglés tipeaba la fecha al revés, y eso en una fecha de entrega comprometida
 * es un compromiso equivocado, no un detalle visual.
 *
 * NACIÓ SOLO CON CALENDARIO Y ESO ERA UN ERROR DE EFICIENCIA, medido en uso
 * real: quien carga varios informes seguidos ya sabe la fecha y teclearla son
 * ocho pulsaciones, mientras que abrir el calendario, ubicar el mes y apuntar
 * al día son tres gestos con el mouse. El calendario sigue estando —es lo que
 * sirve cuando la fecha se PIENSA en vez de saberse, "el jueves de la semana
 * que viene"— pero dejó de ser el único camino.
 *
 * El campo va con MÁSCARA (`enmascararFecha`) y no con texto libre: escribe
 * los guiones sola, descarta lo que no sea un dígito y corta a ocho. Así el
 * usuario no puede producir "10/9/26" ni "septiembre 10", que después habría
 * que adivinar cómo interpretar.
 *
 * SEPARADOR: guion, no barra. Es el formato en el que TODA la aplicación
 * muestra una fecha de calendario —`formatearFecha` acá y
 * `FormatoDelInforme::fechaCorta()` en el API, que lo declara explícito— y un
 * campo que dijera `10/09/2026` sobre un panel que dice `10-09-2026` se leería
 * como dos datos distintos. Tipear la barra igual funciona: la máscara la
 * descarta junto con cualquier otro separador.
 */
export function SelectorDeFecha({
  id,
  value,
  onChange,
  onTextoIncompleto,
  placeholder = "dd-mm-aaaa",
  disabled = false,
  invalido = false,
  className,
}: {
  id?: string;
  /** ISO `yyyy-mm-dd`, o `""` mientras no hay fecha elegida. */
  value: string;
  onChange: (iso: string) => void;
  /**
   * Hay algo tecleado que TODAVÍA no es una fecha.
   *
   * Existe porque `onChange` no alcanza para contarlo: emite `""` tanto para
   * el campo vacío como para el que dice "31-02-2026", y quien valida el
   * formulario necesita separarlos. Sin esto, un campo rojo con basura adentro
   * se enviaría como "sin agendar" y el envío saldría en verde.
   */
  onTextoIncompleto?: (incompleto: boolean) => void;
  placeholder?: string;
  disabled?: boolean;
  invalido?: boolean;
  className?: string;
}) {
  const [abierto, setAbierto] = useState(false);

  // El texto que se ve es estado LOCAL, no `value` formateado, porque a medio
  // escribir no hay ISO que formatear: "10-09-20" no es ninguna fecha y aun así
  // tiene que quedarse en pantalla mientras se termina de teclear.
  const [texto, setTexto] = useState(() => (value ? formatearFecha(value) : ""));

  // Sincronización con el valor que llega DE AFUERA —el calendario, o el padre
  // precargando la cotización vigente—, con el patrón de ajuste en render que
  // recomienda React en vez de un `useEffect`.
  //
  // La comparación con `?? ""` es lo que hace que esto no pise lo que se está
  // tipeando: cuando el texto deja de ser una fecha válida, este componente
  // emite `onChange("")`, y sin tratar `null` y `""` como el mismo "no hay
  // fecha", el valor que vuelve se leería como un cambio externo y borraría el
  // campo en mitad de la palabra.
  const [valorPrevio, setValorPrevio] = useState(value);
  if (value !== valorPrevio) {
    setValorPrevio(value);
    if (value !== (isoDesdeTextoChileno(texto) ?? "")) {
      setTexto(value ? formatearFecha(value) : "");
    }
  }

  const seleccionada = value ? fechaDesdeIso(value) : null;

  // Hay texto y no es una fecha. Se marca acá y no solo desde el padre porque
  // el padre recibe `""` —lo mismo que "sin agendar"— y no tiene cómo
  // distinguir el campo vacío del campo con "31-02-2026" adentro.
  const textoIncompleto = texto !== "" && isoDesdeTextoChileno(texto) === null;

  // El calendario abre en el mes de la fecha elegida; sin fecha, en el actual.
  const anioActual = new Date().getFullYear();

  function alTipear(crudo: string) {
    const enmascarado = enmascararFecha(crudo);
    const iso = isoDesdeTextoChileno(enmascarado);

    setTexto(enmascarado);
    // `""` tanto para el campo vacío como para el que está a medio escribir:
    // en los dos casos, lo que hay para guardar es nada. Lo que los separa es
    // `onTextoIncompleto`, que además pinta el borde.
    onChange(iso ?? "");
    onTextoIncompleto?.(enmascarado !== "" && iso === null);
  }

  return (
    <div className={cn("relative", className)}>
      <Input
        id={id}
        value={texto}
        onChange={(evento) => alTipear(evento.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={invalido || textoIncompleto}
        /* `inputMode="numeric"` y no `type="number"`: en un móvil abre el
           teclado de dígitos, que es lo único que la máscara acepta, pero sin
           las flechas de incremento ni el `valueAsNumber` de un campo
           numérico, que acá no significan nada. */
        inputMode="numeric"
        autoComplete="off"
        /* `font-mono` para que los dígitos no bailen de ancho mientras se
           teclea, y el hueco a la derecha para el botón del calendario. */
        className="pr-12 font-mono"
      />

      <Popover open={abierto} onOpenChange={setAbierto}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={disabled}
            /* El botón tiene nombre accesible propio: es un control aparte del
               campo, y sin él un lector de pantalla anuncia "botón" a secas. */
            aria-label="Abrir el calendario"
            className="text-muted-foreground absolute top-1/2 right-1 -translate-y-1/2"
          >
            <CalendarIcon className="size-4" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            locale={es}
            selected={seleccionada ?? undefined}
            defaultMonth={seleccionada ?? undefined}
            /* Los desplegables de mes y año evitan que comprometer una entrega a
               seis meses cueste seis clics en la flecha. */
            captionLayout="dropdown"
            startMonth={new Date(anioActual - 1, 0)}
            endMonth={new Date(anioActual + 5, 11)}
            autoFocus
            onSelect={(fecha) => {
              onChange(fecha === undefined ? "" : isoDesdeFecha(fecha));
              // Del calendario no puede salir una fecha a medio escribir, así
              // que elegir ahí LIMPIA lo que hubiera quedado mal tecleado.
              onTextoIncompleto?.(false);
              setAbierto(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
